import React, { useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { toast } from "react-toastify";

import { useMeeting, usePubSub } from "../../config/sdkConfig";

import { useSiteContext } from "../../context/SiteContext";
import { getApiUrl } from "../../services/httpServices";

const LeadCaptureViewer = ({ leadCapture, isOpen, onClose, onSubmitted, isModeratorView = false, onEndForm }) => {
  const mMeeting = useMeeting();
  const { user, meetingSession, meetingSettings } = useSiteContext();
  const { publish: publishLeadCapture } = usePubSub("LEAD_CAPTURE");
  const [formValues, setFormValues] = useState({});
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Reset state when lead capture changes
  useEffect(() => {
    if (leadCapture) {
      // Initialize form values with empty strings for each field
      const initialValues = {};
      leadCapture.formConfig?.fields?.forEach((field) => {
        initialValues[field.name] = "";
      });
      setFormValues(initialValues);
      setConsentChecked(false);
      setHasSubmitted(false);
      setShowSuccess(false);
    }
  }, [leadCapture?.id]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleInputChange = (fieldName, value) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const validateForm = () => {
    const fields = leadCapture?.formConfig?.fields || [];
    for (const field of fields) {
      if (field.required && !formValues[field.name]?.trim()) {
        toast.error(`${field.label} is required`);
        return false;
      }
      // Email validation
      if (field.type === "email" && formValues[field.name]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formValues[field.name])) {
          toast.error("Please enter a valid email address");
          return false;
        }
      }
      // URL validation
      if (field.type === "url" && formValues[field.name]) {
        try {
          new URL(formValues[field.name]);
        } catch {
          toast.error("Please enter a valid URL");
          return false;
        }
      }
    }
    // Check consent if required
    if (leadCapture?.consent?.enabled && leadCapture?.consent?.required && !consentChecked) {
      toast.error("Please accept the consent checkbox to continue");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (hasSubmitted) return;

    const participantId = mMeeting?.localParticipant?.id;
    const participantName =
      mMeeting?.localParticipant?.displayName || user?.name || "Anonymous";

    setIsSubmitting(true);

    const submissionData = {
      leadCaptureId: leadCapture.id || leadCapture._id,
      leadCaptureName: leadCapture.name,
      leadCaptureType: leadCapture.type,
      sessionId: meetingSession,
      meetingId: mMeeting?.meetingId,
      roomId: meetingSettings?.roomId || meetingSettings?._id,
      participantId,
      participantName,
      formData: formValues,
      consentGiven: consentChecked,
      timestamp: Date.now(),
    };

    try {
      // Save lead submission to database via API
      const apiPayload = {
        ...submissionData,
        webhookUrl: leadCapture?.crmIntegration?.enabled ? leadCapture?.crmIntegration?.webhookUrl : null,
      };

      try {
        const apiUrl = getApiUrl();
        const response = await fetch(`${apiUrl}/leads/submit`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(apiPayload),
        });

        const result = await response.json();
        if (!result.success) {
          console.warn("Lead API submission warning:", result.message);
        } else {
          console.log("Lead saved to database:", result.data?.id);
        }
      } catch (apiError) {
        console.warn("Lead API submission failed:", apiError);
        // Continue anyway - we'll still broadcast via PubSub
      }

      // If webhook is configured and not handled by API, try to send to webhook directly
      if (leadCapture?.crmIntegration?.enabled && leadCapture?.crmIntegration?.webhookUrl) {
        try {
          await fetch(leadCapture.crmIntegration.webhookUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            mode: "no-cors", // Webhooks may not support CORS
            body: JSON.stringify({
              ...submissionData,
              source: "rlink-meeting",
              formName: leadCapture.name,
            }),
          });
        } catch (webhookError) {
          console.warn("Webhook submission may have failed:", webhookError);
          // Continue anyway - we'll still broadcast via PubSub
        }
      }

      // Publish via PubSub so host can see the submission
      await publishLeadCapture({
        type: "submission",
        leadCaptureId: leadCapture.id || leadCapture._id,
        submission: submissionData,
      });

      setHasSubmitted(true);
      setShowSuccess(true);
      toast.success("Form submitted successfully!");

      if (onSubmitted) {
        onSubmitted(leadCapture.id || leadCapture._id, submissionData);
      }

      // Handle post-submission behavior
      const submitBehavior = leadCapture.submitBehavior || {};

      // Trigger download if configured
      if (submitBehavior.downloadTrigger && leadCapture.formConfig?.downloadUrl) {
        window.open(leadCapture.formConfig.downloadUrl, "_blank");
      }

      // Redirect if configured
      if (submitBehavior.redirectUrl) {
        setTimeout(() => {
          window.open(submitBehavior.redirectUrl, "_blank");
        }, 2000);
      }

      // Auto-close if configured
      if (submitBehavior.closeFormOnSubmit) {
        setTimeout(() => {
          handleClose();
        }, 3000);
      }
    } catch (error) {
      console.error("Error submitting lead capture form:", error);
      toast.error(error.message || "An error occurred while submitting the form.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    if (publishLeadCapture) {
      publishLeadCapture({
        type: "skipped",
        leadCaptureId: leadCapture?.id || leadCapture?._id,
        participantId: mMeeting?.localParticipant?.id,
        participantName: mMeeting?.localParticipant?.displayName || user?.name || "Anonymous",
        timestamp: Date.now(),
      });
    }
    handleClose();
  };

  if (!leadCapture) return null;

  const formConfig = leadCapture.formConfig || {};
  const branding = leadCapture.branding || {};
  const consent = leadCapture.consent || {};
  const submitBehavior = leadCapture.submitBehavior || {};

  // Get branding colors
  const primaryColor = branding.primaryColor || "#8B5CF6";
  const buttonColor = branding.buttonColor || "#8B5CF6";
  const backgroundColor = branding.backgroundColor || "#1F2937";

  const renderField = (field) => {
    const value = formValues[field.name] || "";
    const baseClasses =
      "w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/10 border border-white/20 rounded-lg text-white text-sm sm:text-base placeholder-gray-400 focus:outline-none focus:ring-2 transition-all";

    switch (field.type) {
      case "textarea":
        return (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            disabled={hasSubmitted || isSubmitting}
            className={`${baseClasses} h-24 resize-none ${hasSubmitted || isSubmitting ? "opacity-50" : ""}`}
            style={{ "--tw-ring-color": primaryColor }}
          />
        );
      case "select":
        return (
          <select
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            disabled={hasSubmitted || isSubmitting}
            className={`${baseClasses} ${hasSubmitted || isSubmitting ? "opacity-50" : ""}`}
          >
            <option value="">{field.placeholder || "Select..."}</option>
            {field.options?.map((opt, idx) => (
              <option key={idx} value={opt.value || opt}>
                {opt.label || opt}
              </option>
            ))}
          </select>
        );
      case "checkbox":
        return (
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={value === "true" || value === true}
              onChange={(e) => handleInputChange(field.name, e.target.checked)}
              disabled={hasSubmitted || isSubmitting}
              className="w-5 h-5 rounded border-white/20 text-purple-500 focus:ring-purple-500"
            />
            <span className="text-white text-sm">{field.placeholder || field.label}</span>
          </label>
        );
      case "number":
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            disabled={hasSubmitted || isSubmitting}
            className={`${baseClasses} ${hasSubmitted || isSubmitting ? "opacity-50" : ""}`}
          />
        );
      case "phone":
        return (
          <input
            type="tel"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder || "Phone number"}
            disabled={hasSubmitted || isSubmitting}
            className={`${baseClasses} ${hasSubmitted || isSubmitting ? "opacity-50" : ""}`}
          />
        );
      case "url":
        return (
          <input
            type="url"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder || "https://..."}
            disabled={hasSubmitted || isSubmitting}
            className={`${baseClasses} ${hasSubmitted || isSubmitting ? "opacity-50" : ""}`}
          />
        );
      case "email":
        return (
          <input
            type="email"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder || "you@example.com"}
            disabled={hasSubmitted || isSubmitting}
            className={`${baseClasses} ${hasSubmitted || isSubmitting ? "opacity-50" : ""}`}
          />
        );
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            disabled={hasSubmitted || isSubmitting}
            className={`${baseClasses} ${hasSubmitted || isSubmitting ? "opacity-50" : ""}`}
          />
        );
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0" style={{ zIndex: 9999 }} onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-60" style={{ zIndex: 9999 }} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 10000 }}>
          <div className="flex min-h-full items-center justify-center p-2 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="w-full max-w-md transform overflow-hidden rounded-2xl p-4 sm:p-6 text-left align-middle shadow-xl transition-all max-h-[85dvh] overflow-y-auto"
                style={{ backgroundColor }}
              >
                {/* Close button */}
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>

                {/* Success State */}
                {showSuccess ? (
                  <div className="text-center py-8">
                    <div
                      className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${primaryColor}20` }}
                    >
                      <svg
                        className="w-8 h-8"
                        style={{ color: primaryColor }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      {submitBehavior.successMessage || "Thank you!"}
                    </h3>
                    <p className="text-gray-400 text-sm">
                      Your information has been submitted successfully.
                    </p>
                    {leadCapture.formConfig?.downloadUrl && submitBehavior.downloadTrigger && (
                      <p className="text-gray-400 text-sm mt-2">
                        Your download should start automatically.
                      </p>
                    )}
                    <button
                      onClick={handleClose}
                      className="mt-6 px-6 py-2 rounded-lg text-white font-medium transition-colors"
                      style={{ backgroundColor: buttonColor }}
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Featured Image */}
                    {branding.imageUrl && (
                      <div className="mb-4 rounded-lg overflow-hidden -mx-6 -mt-6">
                        <img
                          src={branding.imageUrl}
                          alt="Featured"
                          className="w-full h-40 object-cover"
                        />
                      </div>
                    )}

                    {/* Logo */}
                    {branding.logoUrl && (
                      <div className="flex justify-center mb-4">
                        <img
                          src={branding.logoUrl}
                          alt="Logo"
                          className="h-12 object-contain"
                        />
                      </div>
                    )}

                    {/* Headline */}
                    <Dialog.Title
                      className="text-lg sm:text-xl font-bold mb-2"
                      style={{ color: primaryColor }}
                    >
                      {formConfig.headline || "Get Instant Access"}
                    </Dialog.Title>

                    {/* Subheadline */}
                    {formConfig.subheadline && (
                      <p className="text-gray-400 text-sm mb-6">
                        {formConfig.subheadline}
                      </p>
                    )}

                    {/* Form Fields */}
                    <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                      {formConfig.fields?.map((field) => (
                        <div key={field.id}>
                          {field.type !== "checkbox" && (
                            <label className="block text-xs text-gray-400 mb-1">
                              {field.label}{" "}
                              {field.required && (
                                <span className="text-red-400">*</span>
                              )}
                            </label>
                          )}
                          {renderField(field)}
                        </div>
                      ))}
                    </div>

                    {/* Consent Checkbox */}
                    {consent.enabled && (
                      <label className="flex items-start gap-3 mb-6 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={consentChecked}
                          onChange={(e) => setConsentChecked(e.target.checked)}
                          disabled={hasSubmitted || isSubmitting}
                          className="mt-0.5 w-4 h-4 rounded border-white/20"
                        />
                        <span className="text-gray-400 text-xs">
                          {consent.text || "I agree to receive marketing emails"}
                          {consent.required && (
                            <span className="text-red-400"> *</span>
                          )}
                          {consent.privacyPolicyUrl && (
                            <>
                              {" "}
                              <a
                                href={consent.privacyPolicyUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline hover:text-white"
                              >
                                Privacy Policy
                              </a>
                            </>
                          )}
                        </span>
                      </label>
                    )}

                    {/* Action Buttons */}
                    <>
                      <div className="flex gap-2 sm:gap-3">
                        <button
                          onClick={handleSubmit}
                          disabled={isSubmitting}
                          className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-white text-sm sm:text-base font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ backgroundColor: buttonColor }}
                        >
                          {isSubmitting
                            ? "Submitting..."
                            : formConfig.buttonText || "Submit"}
                        </button>
                      </div>

                      {/* Calendar Booking Link */}
                      {leadCapture.type === "calendar_booking" &&
                        formConfig.calendarUrl && (
                          <div className="mt-4 text-center">
                            <a
                              href={formConfig.calendarUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm underline hover:no-underline"
                              style={{ color: primaryColor }}
                            >
                              Or book directly on my calendar
                            </a>
                          </div>
                        )}
                    </>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default LeadCaptureViewer;
